import { CheckCircle2, ArrowRight } from "lucide-react";
import { DOMAINS } from "@/lib/domains";

const testid = (id) => `domain-selector-${id}`;

export const DomainHero = ({ onSelect }) => {
  return (
    <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
      <div className="max-w-3xl">
        <p className="mono text-xs tracking-[0.35em] text-cyan-400 uppercase mb-4">
          Select Governed Process
        </p>
        <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-100 leading-[1.05]">
          Simulate a high-consequence
          <span className="text-cyan-400"> bioprocess</span> under
          deterministic governance.
        </h2>
        <p className="mt-5 text-sm sm:text-base text-slate-400 leading-relaxed max-w-2xl">
          Choose a domain to load pre-seeded telemetry. The governor enforces
          Lyapunov stability in real time, quarantines unsafe commands, and
          seals every human-approved correction into the audit ledger.
        </p>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
        {DOMAINS.map((d, i) => {
          const Icon = d.icon;
          return (
            <button
              key={d.id}
              data-testid={testid(d.id)}
              onClick={() => onSelect(d)}
              style={{ animationDelay: `${i * 90}ms` }}
              className="group text-left rounded-2xl overflow-hidden border border-slate-800 bg-slate-900/60 hover:bg-slate-900 hover:border-cyan-600/60 transition-all duration-300 hover:-translate-y-1 hover:cyan-glow focus:outline-none focus:ring-2 focus:ring-cyan-500/60"
            >
              <div className="relative h-40 overflow-hidden">
                <img
                  src={d.image}
                  alt={d.name}
                  className="h-full w-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                <div
                  className="absolute top-3 left-3 h-10 w-10 rounded-lg flex items-center justify-center border"
                  style={{
                    backgroundColor: `${d.accent}22`,
                    borderColor: `${d.accent}66`,
                  }}
                >
                  <Icon className="h-5 w-5" style={{ color: d.accent }} />
                </div>
                <span className="absolute top-4 right-4 mono text-[10px] tracking-widest text-slate-300/80">
                  {d.code}
                </span>
              </div>
              <div className="p-5">
                <h3 className="text-base font-bold text-slate-100 leading-snug">
                  {d.name}
                </h3>
                <p className="mt-2 text-xs text-slate-400 leading-relaxed min-h-[48px]">
                  {d.description}
                </p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex gap-3 mono text-[10px] text-slate-500">
                    <span>pH {d.baselines.ph}</span>
                    <span>{d.baselines.temp}°C</span>
                    <span>O₂ {d.baselines.do2}%</span>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-semibold text-cyan-400 group-hover:gap-2 transition-all">
                    Engage <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const DomainBar = ({ active, onSelect }) => {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {DOMAINS.map((d) => {
        const Icon = d.icon;
        const isActive = d.id === active.id;
        return (
          <button
            key={d.id}
            data-testid={testid(d.id)}
            onClick={() => onSelect(d)}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 mono text-xs font-medium transition-all ${
              isActive
                ? "border-cyan-500/70 bg-cyan-950/40 text-cyan-300 cyan-glow"
                : "border-slate-800 bg-slate-900/50 text-slate-400 hover:border-slate-700 hover:text-slate-200"
            }`}
          >
            <Icon className="h-4 w-4" style={{ color: isActive ? d.accent : undefined }} />
            <span className="hidden sm:inline">{d.short}</span>
            {isActive && <CheckCircle2 className="h-3.5 w-3.5 text-cyan-400" />}
          </button>
        );
      })}
    </div>
  );
};
